import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronLeft, Search, X } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ListRow } from "@/components/ui/list-row";
import { useAuth } from "@/contexts/AuthContext";
import { vociDi } from "@/lib/accessi";
import { GRUPPI, GUIDE, cerca, guideDi } from "@/guide/guide";
import { ReactComponent as LogoIdA } from "@/components/layout/logo-ida.svg";
import { cn } from "@/lib/utils";

/* Le guide (c33). A 1440 elenco a sinistra e guida a destra; sotto i 1024, dove il gestionale ha la barra in
   basso, o l'elenco o la guida, con «Tutte le guide» per tornare. Le foto sono due per passo: quelle del
   telefono e quelle del computer, scelte da <picture> con lo stesso confine del menu (1024). */

const meta = (g) => `${g.passi.length} passi`;

function Passo({ guida, passo, n }) {
  const [rotta, setRotta] = useState(false);
  const base = `/guide-foto/${guida.id}/${n}`;
  return (
    <li className="grid gap-group" data-testid={`guida-passo-${n}`}>
      {passo.parte ? <h3 className="text-card-title text-ink-strong">{passo.parte}</h3> : null}
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-brand text-body-strong text-on-brand"
        >
          {n}
        </span>
        <p className="pt-1 text-base text-ink">
          <span className="sr-only">Passo {n}. </span>
          {passo.testo}
        </p>
      </div>
      {rotta || !passo.punta ? null : (
        <picture>
          <source media="(min-width: 1024px)" srcSet={`${base}-1440.jpg`} width="960" height="600" />
          <img
            src={`${base}-390.jpg`}
            width="390"
            height="520"
            loading="lazy"
            alt=""
            onError={() => setRotta(true)}
            className="h-auto w-full rounded-lg border border-line bg-surface-2"
          />
        </picture>
      )}
    </li>
  );
}

export function GuidaDettaglio({ guida, ritorno }) {
  const gruppo = GRUPPI.find((x) => x.id === guida.gruppo);
  return (
    <article className="grid gap-section rounded-xl border border-line bg-surface p-4 md:p-card" data-testid="guida" data-guida={guida.id}>
      <header className="grid gap-inside">
        {ritorno}
        <p className="text-small text-ink-3">
          {gruppo?.titolo} · {meta(guida)}
        </p>
        <h2 className="text-balance font-display text-h2 text-ink-strong">{guida.titolo}</h2>
        <p className="text-base text-ink-2">{guida.quando}</p>
      </header>
      <ol className="grid gap-section">
        {guida.passi.map((p, i) => (
          <Passo key={`${guida.id}-${i}`} guida={guida} passo={p} n={i + 1} />
        ))}
      </ol>
      <div className="grid gap-card border-t border-line-subtle pt-card">
        <section className="grid gap-inside">
          <h3 className="text-h4 text-ink-strong">Cosa succede dopo</h3>
          <p className="text-base text-ink-2">{guida.dopo}</p>
        </section>
        <section className="grid gap-inside">
          <h3 className="text-h4 text-ink-strong">Se qualcosa non va</h3>
          <p className="text-base text-ink-2">{guida.seNonVa}</p>
        </section>
        {guida.vai ? (
          <Button asChild className="w-full sm:w-auto sm:justify-self-start">
            <Link to={guida.vai.to} data-testid="guida-vai">
              {guida.vai.label}
            </Link>
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function Cerca({ valore, onCambia }) {
  return (
    <div className="relative w-full sm:w-80">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
      <Input
        type="search"
        value={valore}
        onChange={(e) => onCambia(e.target.value)}
        placeholder="Cerca una guida"
        aria-label="Cerca una guida"
        className="pl-9 pr-11 [&::-webkit-search-cancel-button]:hidden"
        data-testid="guide-cerca"
      />
      {valore ? (
        <button
          type="button"
          onClick={() => onCambia("")}
          aria-label="Cancella la ricerca"
          className="absolute right-0.5 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-sm text-ink-2 hover:text-ink md:size-9"
        >
          <X className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

function Elenco({ guide, scelta, q }) {
  if (!guide.length)
    return (
      <p className="rounded-xl border border-line bg-surface p-4 text-base text-ink-2 md:p-card" data-testid="guide-nessuna">
        Nessuna guida con «{q.trim()}». Prova con un'altra parola, per esempio «preventivo» o «visita».
      </p>
    );
  return GRUPPI.map((gr) => {
    const sue = guide.filter((g) => g.gruppo === gr.id);
    if (!sue.length) return null;
    return (
      <section key={gr.id} className="overflow-hidden rounded-xl border border-line bg-surface" data-testid={`guide-gruppo-${gr.id}`}>
        <h2 className="border-b border-line-subtle px-4 py-3 text-card-title text-ink-strong">{gr.titolo}</h2>
        {sue.map((g) => (
          <ListRow
            key={g.id}
            href={`/guide/${g.id}`}
            title={g.titolo}
            meta={meta(g)}
            selected={g.id === scelta}
            className="last:border-b-0"
            data-testid={`guide-voce-${g.id}`}
          />
        ))}
      </section>
    );
  });
}

export default function GuidePage() {
  const { id } = useParams();
  const { profile } = useAuth() || {};
  const [q, setQ] = useState("");
  const mie = guideDi(profile?.ruolo, vociDi(profile?.ruolo, profile?.sezioni_spente));
  const guida = id ? mie.find((g) => g.id === id) : null;
  // Una guida si apre dall'inizio, anche se l'elenco era scorso. Le graffe servono: in Chrome recente scrollTo
  // restituisce una Promise, e un effetto che restituisce qualcosa che non è una funzione rompe la pagina.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  const tutte = (
    <Link
      to="/guide"
      className="-ml-2 inline-flex h-11 items-center gap-1 justify-self-start rounded-sm px-2 text-body font-semibold text-ink-2 transition-colors duration-150 ease-enter hover:bg-surface-3 hover:text-ink-strong lg:hidden"
      data-testid="guida-tutte"
    >
      <ChevronLeft className="size-4" aria-hidden />
      Tutte le guide
    </Link>
  );

  return (
    <section data-testid="page-guide" className="grid gap-card">
      <div className={cn(id && "hidden lg:block")}>
        <PageHeader
          title="Guide"
          description="Come si fanno le cose nel gestionale, passo per passo, con le foto di cosa premere."
          actions={<Cerca valore={q} onCambia={setQ} />}
        />
      </div>
      <div className="grid items-start gap-card lg:grid-cols-[360px_minmax(0,1fr)]">
        <nav aria-label="Elenco delle guide" className={cn("grid gap-card", id && "hidden lg:grid")}>
          <Elenco guide={cerca(mie, q)} scelta={id} q={q} />
        </nav>
        {guida ? (
          <GuidaDettaglio guida={guida} ritorno={tutte} />
        ) : id ? (
          <div className="grid gap-group rounded-xl border border-line bg-surface p-4 md:p-card" data-testid="guida-assente">
            {tutte}
            <p className="text-base text-ink-2">Questa guida non c'è, o non è fra quelle che puoi aprire. Le altre sono nell'elenco.</p>
          </div>
        ) : (
          <p className="hidden rounded-xl border border-dashed border-line p-card text-base text-ink-3 lg:block">
            Scegli una guida dall'elenco.
          </p>
        )}
      </div>
    </section>
  );
}

/* Fuori dal gestionale, senza login: solo le guide `pubblica` (il primo accesso), aperte dalle pagine di accesso. */
export function AiutoPage() {
  const { id } = useParams();
  const guida = GUIDE.find((g) => g.id === id && g.pubblica);
  if (!guida) return <Navigate to="/login" replace />;
  return (
    <main className="min-h-dvh bg-page px-4 py-card md:py-section">
      <div className="mx-auto grid max-w-2xl gap-card">
        <LogoIdA className="h-10 w-auto justify-self-start text-brand" role="img" aria-label="Interni D'Autore" />
        <GuidaDettaglio
          guida={{ ...guida, vai: null }}
          ritorno={
            <Link
              to="/login"
              className="-ml-2 inline-flex h-11 items-center gap-1 justify-self-start rounded-sm px-2 text-body font-semibold text-ink-2 hover:bg-surface-3 hover:text-ink-strong"
            >
              <ChevronLeft className="size-4" aria-hidden />
              Torna all'accesso
            </Link>
          }
        />
      </div>
    </main>
  );
}
